/**
 * The device's history of rounds played together on it, by taking turns
 * (`docs/decisions/0059`): "On this device" on the You tab.
 *
 * **Its own store, apart from every online record.** It has its own key and
 * is read and written only here; nothing in it is ever written to the
 * personal bests (`state/personal-bests-io.ts`), the solo statistics
 * (`state/solo-stats-io.ts`), the daily or a leaderboard, and nothing from
 * those is read here. For a signed-in device owner it also syncs with the
 * account (`state/local-history.svelte.ts`, `PUT /api/account/local-sessions`);
 * for a guest it stays on the device.
 *
 * **Every round belongs to one player** (0059, "After review"). A shared
 * device sees several people sign in and out, and an account's history is a
 * union that never deletes, so a round that reached the wrong account would
 * stay there for good. Each stored round therefore carries its `owner`: the
 * id of the account it was played under or came down from, or `"guest"` for a
 * round played while nobody was signed in. The store also remembers which
 * account the device is signed in as (`account`), so a round played while the
 * server cannot be reached still goes to the person who never signed out.
 *
 * - What the You tab shows, and what a new round is filed under, is the
 *   current owner's: the signed-in account's, or the guest's.
 * - Signing in as P (`bindLocalHistory`) makes the guest's rounds P's; only
 *   P's rounds are ever uploaded to P (`uploadFor`). Another account's rounds
 *   stay where they are and never leave the device.
 * - Signing out (`releaseLocalHistory`) drops the account's rounds when the
 *   server is known to hold them, and otherwise parks them, hidden, until
 *   that account signs in here again.
 * - Rounds written by a build from before owners (`interlock:local-sessions:v1`)
 *   have none: nobody can say whose they are, so they are dropped on the
 *   first load and never uploaded.
 *
 * Bounded: the newest `LOCAL_SESSION_LIMIT` (50) rounds per owner, by the one
 * merge rule the server applies too (`@interlock/protocol`'s
 * `mergeLocalSessions`), and `LOCAL_HISTORY_DEVICE_LIMIT` (200) on the whole
 * device, the current owner's kept first, so parked rounds cannot grow
 * without limit however many people sign in on it.
 *
 * A plain module with no rune, so it runs under plain Vitest.
 */
import {
  LOCAL_SESSION_LIMIT,
  compareLocalSessions,
  mergeLocalSessions,
  parseLocalSession,
  parseLocalSessions,
} from '@interlock/protocol';
import type { LocalSession } from '@interlock/protocol';

import { safeLocalStorage } from '../state/settings-io.js';
import { listNames } from './round.js';

/** Version 2: every round carries its owner. */
export const LOCAL_HISTORY_STORAGE_KEY = 'interlock:local-sessions:v2';
/** What older builds wrote, with no owner: never read, removed on the first load. */
export const LEGACY_LOCAL_HISTORY_STORAGE_KEYS: readonly string[] = ['interlock:local-sessions:v1'];

/** The owner of a round played while nobody was signed in. */
export const GUEST_OWNER = 'guest';

/** The most rounds the device keeps, across every owner. */
export const LOCAL_HISTORY_DEVICE_LIMIT = 200;

/** An account id as the server writes one (a UUID), or `guest`. */
const OWNER_PATTERN = /^[A-Za-z0-9-]{1,64}$/;

/** One stored round, and whose it is. */
export interface OwnedLocalSession {
  /** An account id, or `GUEST_OWNER`. */
  readonly owner: string;
  readonly session: LocalSession;
}

/** The whole device store. */
export interface LocalHistoryStore {
  /** The account the device last knew it was signed in as; `null` for nobody. */
  readonly account: string | null;
  /** Newest first. */
  readonly rounds: readonly OwnedLocalSession[];
}

const EMPTY: LocalHistoryStore = { account: null, rounds: [] };

function isOwner(value: unknown): value is string {
  return typeof value === 'string' && OWNER_PATTERN.test(value);
}

/** Whose rounds the device shows, and files a new round under. */
export function ownerOf(store: LocalHistoryStore): string {
  return store.account ?? GUEST_OWNER;
}

/** One owner's rounds, newest first: by default the current owner's. */
export function sessionsOf(
  store: LocalHistoryStore,
  owner: string = ownerOf(store),
): LocalSession[] {
  return store.rounds.filter((round) => round.owner === owner).map((round) => round.session);
}

/**
 * What may go up to `account`: that account's rounds and nothing else. (A
 * guest's rounds became the account's when it signed in, `bindLocalHistory`.)
 */
export function uploadFor(store: LocalHistoryStore, account: string): LocalSession[] {
  return account === GUEST_OWNER ? [] : sessionsOf(store, account);
}

function compareOwned(a: OwnedLocalSession, b: OwnedLocalSession): number {
  const bySession = compareLocalSessions(a.session, b.session);
  if (bySession !== 0) return bySession;
  return a.owner < b.owner ? -1 : a.owner > b.owner ? 1 : 0;
}

/**
 * The bounds, applied every time the store is read or written: each owner's
 * rounds a union by id (the first copy kept) cut to its newest fifty; then
 * the current owner's all kept, and everyone else's, newest first, up to the
 * device limit.
 */
function bounded(account: string | null, rounds: readonly OwnedLocalSession[]): LocalHistoryStore {
  const byOwner = new Map<string, LocalSession[]>();
  for (const round of rounds) {
    const list = byOwner.get(round.owner) ?? [];
    list.push(round.session);
    byOwner.set(round.owner, list);
  }
  const current = account ?? GUEST_OWNER;
  const mine: OwnedLocalSession[] = [];
  const others: OwnedLocalSession[] = [];
  for (const [owner, sessions] of byOwner) {
    for (const session of mergeLocalSessions([sessions], LOCAL_SESSION_LIMIT)) {
      (owner === current ? mine : others).push({ owner, session });
    }
  }
  others.sort(compareOwned);
  const room = Math.max(0, LOCAL_HISTORY_DEVICE_LIMIT - mine.length);
  return { account, rounds: [...mine, ...others.slice(0, room)].sort(compareOwned) };
}

function parseStore(raw: unknown): LocalHistoryStore {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return EMPTY;
  const entry = raw as Record<string, unknown>;
  const rawAccount = entry['account'];
  const account = isOwner(rawAccount) && rawAccount !== GUEST_OWNER ? rawAccount : null;
  const rawRounds = entry['rounds'];
  const rounds: OwnedLocalSession[] = [];
  if (Array.isArray(rawRounds)) {
    for (const item of rawRounds.slice(0, LOCAL_HISTORY_DEVICE_LIMIT * 2)) {
      if (typeof item !== 'object' || item === null) continue;
      const { owner, session } = item as Record<string, unknown>;
      // A round with no owner, or an owner nobody could have written, is
      // nobody's: dropped, never shown and never uploaded.
      if (!isOwner(owner)) continue;
      const parsed = parseLocalSession(session);
      if (parsed !== null) rounds.push({ owner, session: parsed });
    }
  }
  return bounded(account, rounds);
}

/** The device's store. Anything unreadable is dropped, and so is an older build's ownerless copy. */
export function loadLocalHistory(
  storage: Storage | undefined = safeLocalStorage(),
): LocalHistoryStore {
  if (storage === undefined) return EMPTY;
  try {
    for (const key of LEGACY_LOCAL_HISTORY_STORAGE_KEYS) {
      if (storage.getItem(key) !== null) storage.removeItem(key);
    }
    const raw = storage.getItem(LOCAL_HISTORY_STORAGE_KEY);
    if (raw === null) return EMPTY;
    return parseStore(JSON.parse(raw));
  } catch {
    return EMPTY;
  }
}

/** The current owner's rounds, newest first: what the You tab lists. */
export function loadLocalSessions(
  storage: Storage | undefined = safeLocalStorage(),
): LocalSession[] {
  return sessionsOf(loadLocalHistory(storage));
}

function write(store: LocalHistoryStore, storage: Storage | undefined): LocalHistoryStore {
  if (storage === undefined) return store;
  try {
    storage.setItem(LOCAL_HISTORY_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Storage full or blocked (private browsing): the policy of every local
    // store here. Stop persisting, never interrupt play.
  }
  return store;
}

/** A round just played, filed under the current owner. Returns the store as it now stands. */
export function addLocalSession(
  session: LocalSession,
  storage: Storage | undefined = safeLocalStorage(),
): LocalHistoryStore {
  const store = loadLocalHistory(storage);
  return write(
    bounded(store.account, [{ owner: ownerOf(store), session }, ...store.rounds]),
    storage,
  );
}

/**
 * The device is signed in as `account` (a sign-in, or a load that found a
 * session): it becomes the current owner, and the guest's rounds become its
 * rounds. Another account's rounds are left exactly as they are.
 */
export function bindLocalHistory(
  account: string,
  storage: Storage | undefined = safeLocalStorage(),
): LocalHistoryStore {
  const store = loadLocalHistory(storage);
  if (!isOwner(account) || account === GUEST_OWNER) return store;
  if (store.account === account && !store.rounds.some((round) => round.owner === GUEST_OWNER)) {
    return store;
  }
  const rounds = store.rounds.map((round) =>
    round.owner === GUEST_OWNER ? { owner: account, session: round.session } : round,
  );
  return write(bounded(account, rounds), storage);
}

/**
 * The device is signed out. With `drop`, the account's rounds leave the
 * device (the server holds them and hands them back on the next sign-in);
 * without, they stay, hidden and never uploaded to anyone else, until that
 * account signs in here again. Either way the guest is the owner after.
 */
export function releaseLocalHistory(
  drop: boolean,
  storage: Storage | undefined = safeLocalStorage(),
): LocalHistoryStore {
  const store = loadLocalHistory(storage);
  const account = store.account;
  if (account === null) return store;
  const rounds = drop ? store.rounds.filter((round) => round.owner !== account) : store.rounds;
  return write(bounded(null, rounds), storage);
}

/**
 * Rounds from `account`, merged into its own rounds on the device (a union by
 * id, the newest fifty kept). Only while the device is still signed in as
 * that account: an answer that arrives after a sign-out, or after someone
 * else signed in, is not written, so it cannot bring the rounds back.
 */
export function adoptLocalSessions(
  account: string,
  incoming: readonly LocalSession[],
  storage: Storage | undefined = safeLocalStorage(),
): LocalHistoryStore {
  const store = loadLocalHistory(storage);
  if (store.account !== account) return store;
  const owned = parseLocalSessions(incoming, LOCAL_SESSION_LIMIT).map((session) => ({
    owner: account,
    session,
  }));
  return write(bounded(account, [...store.rounds, ...owned]), storage);
}

/** One name's wins across the history. */
export interface NameWins {
  readonly name: string;
  readonly wins: number;
}

/** The simple totals the You tab shows over the list. */
export interface LocalTotals {
  readonly rounds: number;
  readonly turns: number;
  /** Most wins first, then by name; a shared win counts for each winner; only names with a win. */
  readonly wins: readonly NameWins[];
}

/**
 * Rounds, turns and wins by name. A name is counted as written (names are
 * the players' own words, not accounts), case-insensitively so "Noor" and
 * "noor" are one person; the spelling kept is the most recent round's.
 */
export function localTotals(sessions: readonly LocalSession[]): LocalTotals {
  const wins = new Map<string, NameWins>();
  let turns = 0;
  for (const session of sessions) {
    turns += session.players.length;
    for (const player of session.players) {
      if (player.rank !== 1 || player.outcome === 'quit') continue;
      const key = player.name.toLocaleLowerCase();
      const held = wins.get(key);
      wins.set(key, { name: held?.name ?? player.name, wins: (held?.wins ?? 0) + 1 });
    }
  }
  return {
    rounds: sessions.length,
    turns,
    wins: [...wins.values()].sort(
      (a, b) => b.wins - a.wins || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    ),
  };
}

/** The players a round's winners were: rank 1, and played on (a quit never wins). */
export function sessionWinners(session: LocalSession): string[] {
  return session.players
    .filter((player) => player.rank === 1 && player.outcome !== 'quit')
    .map((player) => player.name);
}

/** "Noor won", "Noor and Sam tied", "Nobody played on": a round in one line. */
export function sessionHeadline(session: LocalSession): string {
  const winners = sessionWinners(session);
  if (winners.length === 0) return 'Nobody played on';
  if (winners.length === 1) return `${winners[0] ?? ''} won`;
  return `${listNames(winners)} tied`;
}
